import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import Layout from './Layout';
import ListPager from '../components/ListPager';

const API = process.env.REACT_APP_API_URL || 'http://127.0.0.1:8000';
const LIMIT = 20;
const STATUS_LABEL = { open: 'Открыт', closed: 'Закрыт' };
const KIND_LABEL = { doctor: 'Врач', clinic: 'Клиника', network: 'Сеть' };

function fmtDate(str) {
  if (!str) return '';
  return new Date(str).toLocaleString('ru', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

export default function Tickets() {
  const [status, setStatus] = useState('open');
  const [search, setSearch] = useState('');
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [selectedId, setSelectedId] = useState(null);
  const [ticket, setTicket] = useState(null);
  const [reply, setReply] = useState('');
  const [err, setErr] = useState('');
  const [sending, setSending] = useState(false);

  const key = localStorage.getItem('adminKey');
  const headers = { 'X-Admin-Key': key };

  const fetchItems = useCallback(() => {
    axios.get(`${API}/admin/support/tickets`, { headers, params: { status: status || undefined, search: search || undefined, limit: LIMIT, offset } })
      .then(r => { setItems(r.data.items); setTotal(r.data.total); });
  }, [status, search, offset]); // eslint-disable-line
  useEffect(() => { fetchItems(); }, [fetchItems]);

  const loadTicket = (id) => axios.get(`${API}/admin/support/tickets/${id}`, { headers }).then(r => setTicket(r.data));
  const openTicket = (id) => { setSelectedId(id); setErr(''); loadTicket(id); };
  const closeDetail = () => { setSelectedId(null); setTicket(null); fetchItems(); };

  const sendReply = async () => {
    if (!reply.trim()) return;
    setSending(true); setErr('');
    try {
      await axios.post(`${API}/admin/support/tickets/${selectedId}/messages`, { body: reply }, { headers });
      setReply('');
      await loadTicket(selectedId);
    } catch (e) { setErr(e?.response?.data?.detail || 'Не удалось отправить'); }
    setSending(false);
  };

  const setTicketStatus = async (newStatus) => {
    await axios.patch(`${API}/admin/support/tickets/${selectedId}/status`, { status: newStatus }, { headers });
    loadTicket(selectedId);
  };

  if (selectedId) {
    return (
      <Layout>
        <button style={styles.backBtn} onClick={closeDetail}>← Все тикеты</button>
        {!ticket ? <div style={styles.empty}>Загрузка…</div> : (
          <>
            <div style={styles.detailHeader}>
              <div>
                <h1 style={styles.title}>{ticket.subject}</h1>
                <div style={styles.senderLine}>{KIND_LABEL[ticket.sender_kind] || ticket.sender_kind}: {ticket.sender_name}</div>
              </div>
              <span style={{ ...styles.badge, ...(ticket.status === 'open' ? styles.badgeTeal : styles.badgeGray) }}>{STATUS_LABEL[ticket.status]}</span>
            </div>

            <div style={styles.thread}>
              {ticket.messages.map(m => (
                <div key={m.id} style={{ ...styles.bubble, ...(m.sender_role === 'admin' ? styles.bubbleAdmin : styles.bubbleUser) }}>
                  <div style={styles.bubbleMeta}>{m.sender_role === 'admin' ? 'Техподдержка' : ticket.sender_name} · {fmtDate(m.created_at)}</div>
                  <div style={styles.bubbleBody}>{m.body}</div>
                </div>
              ))}
            </div>

            <div style={styles.card}>
              <textarea style={styles.textarea} rows={3} placeholder="Ваш ответ…" value={reply} onChange={e => setReply(e.target.value)} />
              {err && <div style={styles.errText}>{err}</div>}
              <div style={styles.actions}>
                <button style={styles.btnGhost} onClick={() => setTicketStatus(ticket.status === 'open' ? 'closed' : 'open')}>
                  {ticket.status === 'open' ? 'Закрыть тикет' : 'Открыть заново'}
                </button>
                <button style={styles.btn} onClick={sendReply} disabled={sending}>{sending ? 'Отправка…' : 'Отправить'}</button>
              </div>
            </div>
          </>
        )}
      </Layout>
    );
  }

  return (
    <Layout>
      <div style={styles.header}>
        <h1 style={styles.title}>Техподдержка</h1>
      </div>

      <div style={styles.filterBar}>
        {[['open', 'Открытые'], ['closed', 'Закрытые'], ['', 'Все']].map(([v, l]) => (
          <button key={l} style={{ ...styles.modeBtn, ...(status === v ? styles.modeBtnActive : {}) }}
            onClick={() => { setStatus(v); setOffset(0); }}>{l}</button>
        ))}
        <input style={styles.search} placeholder="Поиск по теме / отправителю…" value={search}
          onChange={e => { setSearch(e.target.value); setOffset(0); }} />
      </div>

      <div style={styles.list}>
        {items.map(t => (
          <div key={t.id} style={styles.row} onClick={() => openTicket(t.id)}>
            <div style={styles.rowMain}>
              <div style={styles.rowSubject}>{t.subject}</div>
              <div style={styles.rowMeta}>{KIND_LABEL[t.sender_kind] || t.sender_kind}: {t.sender_name} · {fmtDate(t.last_message_at)}</div>
            </div>
            <div style={styles.rowRight}>
              {t.status === 'open' && t.last_message_role === 'user' && <span style={styles.waitTag}>Ждёт ответа</span>}
              <span style={{ ...styles.badge, ...(t.status === 'open' ? styles.badgeTeal : styles.badgeGray) }}>{STATUS_LABEL[t.status]}</span>
            </div>
          </div>
        ))}
        {items.length === 0 && <div style={styles.empty}>Тикетов нет</div>}
      </div>
      <ListPager total={total} limit={LIMIT} offset={offset} onPage={setOffset} />
    </Layout>
  );
}

const styles = {
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 },
  title: { color: 'white', fontSize: 24, fontWeight: 700, margin: 0 },
  filterBar: { display: 'flex', gap: 8, marginBottom: 20, alignItems: 'center', flexWrap: 'wrap' },
  modeBtn: { background: '#1e293b', color: '#94a3b8', border: '1px solid #334155', padding: '7px 16px', borderRadius: 8, fontSize: 13, cursor: 'pointer', fontWeight: 500 },
  modeBtnActive: { background: '#2EC4B6', color: 'white', border: '1px solid #2EC4B6' },
  search: { background: '#1e293b', color: '#e2e8f0', border: '1px solid #334155', borderRadius: 8, padding: '7px 12px', fontSize: 13, outline: 'none', minWidth: 240, marginLeft: 'auto' },
  list: { display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 16 },
  row: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: '#1e293b', borderRadius: 10, padding: '14px 18px', cursor: 'pointer' },
  rowMain: { display: 'flex', flexDirection: 'column', gap: 4 },
  rowSubject: { color: '#e2e8f0', fontWeight: 600, fontSize: 14 },
  rowMeta: { color: '#64748b', fontSize: 12 },
  rowRight: { display: 'flex', alignItems: 'center', gap: 10 },
  waitTag: { color: '#fbbf24', fontSize: 12, fontWeight: 600 },
  badge: { padding: '4px 10px', borderRadius: 20, fontSize: 12, fontWeight: 600 },
  badgeTeal: { background: '#0f3d38', color: '#2EC4B6' },
  badgeGray: { background: '#0f172a', color: '#64748b' },
  empty: { color: '#64748b', fontSize: 14, textAlign: 'center', padding: '32px 0' },
  backBtn: { background: 'transparent', border: 'none', color: '#2EC4B6', fontSize: 13, cursor: 'pointer', fontWeight: 600, padding: 0, marginBottom: 16 },
  detailHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16 },
  senderLine: { color: '#64748b', fontSize: 13, marginTop: 4 },
  thread: { display: 'flex', flexDirection: 'column', gap: 12, marginBottom: 16 },
  bubble: { maxWidth: '70%', borderRadius: 12, padding: '12px 16px' },
  bubbleUser: { background: '#1e293b', alignSelf: 'flex-start' },
  bubbleAdmin: { background: '#0f3d38', alignSelf: 'flex-end' },
  bubbleMeta: { color: '#64748b', fontSize: 11, marginBottom: 4 },
  bubbleBody: { color: '#e2e8f0', fontSize: 14, whiteSpace: 'pre-wrap' },
  card: { background: '#1e293b', borderRadius: 12, padding: 18, display: 'flex', flexDirection: 'column', gap: 8 },
  textarea: { background: '#0f172a', color: '#e2e8f0', border: '1px solid #334155', borderRadius: 8, padding: '10px 12px', fontSize: 14, outline: 'none', resize: 'vertical', fontFamily: 'inherit' },
  errText: { color: '#f87171', fontSize: 13 },
  actions: { display: 'flex', justifyContent: 'flex-end', gap: 8 },
  btn: { background: '#2EC4B6', color: '#0f172a', border: 'none', borderRadius: 6, padding: '9px 18px', fontSize: 13, fontWeight: 600, cursor: 'pointer' },
  btnGhost: { background: 'transparent', color: '#94a3b8', border: '1px solid #334155', borderRadius: 6, padding: '9px 18px', fontSize: 13, cursor: 'pointer' },
};
